/**
 * BoqSettingsPage Component
 * Settings hub for managing custom user BOQ templates and browsing Finishd system starter templates.
 */

import {
	ArrowLeft,
	Copy,
	Eye,
	FileSpreadsheet,
	Layers,
	Pencil,
	Plus,
	Sparkles,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { BoqTemplatePreview } from "@/features/projects/components/boq/boq-template-preview";
import type {
	BOQStarterPreview,
	BOQStarterTemplate,
	CustomBOQTemplate,
} from "@/features/projects/services/boq/boq-api.service";
import {
	customTemplateToPreview,
	deleteCustomTemplate,
	duplicateCustomTemplate,
	getBOQStarterTemplatesForUser,
	getCustomTemplates,
	saveCustomTemplate,
} from "@/features/projects/services/boq/boq-api.service";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { formatPricePaisa } from "@/lib/format";
import { TemplateEditorDialog } from "../components/template-editor-dialog";

export function BoqSettingsPage() {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<"custom" | "system">("custom");
	const [customTemplates, setCustomTemplates] = useState<CustomBOQTemplate[]>(
		[],
	);
	const [systemTemplates, setSystemTemplates] = useState<BOQStarterTemplate[]>(
		[],
	);
	const [systemLoadError, setSystemLoadError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Editor state
	const [isEditorOpen, setIsEditorOpen] = useState(false);
	const [editingTemplate, setEditingTemplate] =
		useState<CustomBOQTemplate | null>(null);

	// Preview state
	const [previewData, setPreviewData] = useState<BOQStarterPreview | null>(
		null,
	);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const loadData = useCallback(async () => {
		try {
			setIsLoading(true);
			const custom = await getCustomTemplates();
			setCustomTemplates(custom);

			// System templates
			const systemRes = await getBOQStarterTemplatesForUser();
			if (systemRes.success) {
				setSystemTemplates(systemRes.templates);
				setSystemLoadError(null);
			} else {
				setSystemTemplates([]);
				setSystemLoadError(
					systemRes.error || "Failed to load starter templates",
				);
			}
		} catch {
			toast.error("Failed to load BOQ templates");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	// Handlers
	const handleCreateNew = () => {
		setEditingTemplate(null);
		setIsEditorOpen(true);
	};

	const handleEdit = (tmpl: CustomBOQTemplate) => {
		setEditingTemplate(tmpl);
		setIsEditorOpen(true);
	};

	const handleDuplicate = async (tmplId: string) => {
		try {
			await duplicateCustomTemplate(tmplId);
			toast.success("Template duplicated");
			await loadData();
		} catch {
			toast.error("Failed to duplicate template");
		}
	};

	const handleDelete = async (tmplId: string) => {
		if (!confirm("Are you sure you want to delete this template?")) return;
		try {
			await deleteCustomTemplate(tmplId);
			toast.success("Template deleted");
			await loadData();
		} catch {
			toast.error("Failed to delete template");
		}
	};

	const handlePreviewCustom = (tmpl: CustomBOQTemplate) => {
		const preview = customTemplateToPreview(tmpl);
		setPreviewData(preview);
		setIsPreviewOpen(true);
	};

	const handleCustomizeSystemTemplate = async (sys: BOQStarterTemplate) => {
		try {
			await saveCustomTemplate({
				name: `${sys.name} (Custom)`,
				slug: `${sys.slug}-custom-${Date.now().toString().slice(-4)}`,
				description: sys.description || "",
				projectScope: sys.projectScope || "full_home",
				defaultTier: sys.defaultTier,
				supportedProviderRoles: sys.supportedProviderRoles,
				isActive: true,
				isSystem: false,
				sections: [
					{
						id: `sec-${Date.now()}-1`,
						name: "Living & Dining",
						sortOrder: 1,
						items: [
							{
								id: `it-${Date.now()}-1`,
								description: "Modular false ceiling with peripheral cove",
								uom: "sq_ft",
								quantity: 280,
								unitPrice: 15500,
								lineTotal: 4340000,
							},
						],
					},
				],
			});
			toast.success("Template customized and saved to My Custom Templates");
			await loadData();
			setActiveTab("custom");
		} catch {
			toast.error("Failed to customize template");
		}
	};

	return (
		<div className="space-y-6">
			{/* Top Header */}
			<FloatPanel elevation={1} padding={24}>
				<div className="flex flex-wrap items-center justify-between gap-4">
					<div className="flex items-center gap-3">
						<StudioButton
							variant="ghost"
							size="sm"
							icon={ArrowLeft}
							onClick={() => navigate("/settings")}
						/>
						<div>
							<div className="flex items-center gap-2">
								<FileSpreadsheet className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
								<h1
									style={{
										fontFamily: "var(--font-display)",
										fontSize: 22,
										fontWeight: 600,
										color: "var(--color-dashboard-text)",
									}}
								>
									Estimates & BOQ Templates
								</h1>
							</div>
							<p
								className="text-xs"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								Create and customize estimation packages to quickly generate
								client-ready project BOQs.
							</p>
						</div>
					</div>

					<div className="flex items-center gap-3">
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => navigate("/boqs")}
						>
							View All BOQs
						</StudioButton>
						<StudioButton
							variant="primary"
							size="sm"
							icon={Plus}
							onClick={handleCreateNew}
						>
							Create Template
						</StudioButton>
					</div>
				</div>
			</FloatPanel>

			{/* Tabs Header */}
			<div
				className="flex items-center gap-2 border-b pb-2"
				style={{ borderColor: "var(--color-dashboard-border)" }}
			>
				<button
					type="button"
					onClick={() => setActiveTab("custom")}
					className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
						activeTab === "custom"
							? "bg-[var(--color-dashboard-surface)] text-[var(--color-dashboard-text)] border border-[var(--color-dashboard-border)] shadow-sm"
							: "text-[var(--color-dashboard-muted)] hover:text-[var(--color-dashboard-text)]"
					}`}
				>
					<Layers className="h-4 w-4" />
					<span>My Custom Templates</span>
					<span
						className="ml-1 rounded-full px-2 py-0.5 text-xs font-semibold"
						style={{
							background: "var(--color-dashboard-divider)",
							color: "var(--color-dashboard-accent-dark)",
						}}
					>
						{customTemplates.length}
					</span>
				</button>

				<button
					type="button"
					onClick={() => setActiveTab("system")}
					className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
						activeTab === "system"
							? "bg-[var(--color-dashboard-surface)] text-[var(--color-dashboard-text)] border border-[var(--color-dashboard-border)] shadow-sm"
							: "text-[var(--color-dashboard-muted)] hover:text-[var(--color-dashboard-text)]"
					}`}
				>
					<Sparkles className="h-4 w-4" />
					<span>Finishd Starter Templates</span>
					<span
						className="ml-1 rounded-full px-2 py-0.5 text-xs font-semibold"
						style={{
							background: "var(--color-dashboard-divider)",
							color: "var(--color-dashboard-accent-dark)",
						}}
					>
						{systemLoadError ? "!" : systemTemplates.length}
					</span>
				</button>
			</div>

			{/* Content Area */}
			{isLoading ? (
				<div className="py-12 text-center text-sm text-[var(--color-dashboard-muted)]">
					Loading templates…
				</div>
			) : activeTab === "custom" ? (
				customTemplates.length === 0 ? (
					<FloatPanel elevation={1} padding={40}>
						<div className="text-center space-y-4">
							<FileSpreadsheet
								className="mx-auto h-12 w-12 text-[var(--color-dashboard-muted)]"
								strokeWidth={1.5}
							/>
							<div className="space-y-1">
								<h3
									className="text-base font-semibold"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									No Custom Templates Yet
								</h3>
								<p
									className="text-xs max-w-sm mx-auto"
									style={{ color: "var(--color-dashboard-muted)" }}
								>
									Author your studio's custom packages or save any existing
									project BOQ as a reusable template.
								</p>
							</div>
							<StudioButton
								variant="primary"
								size="sm"
								icon={Plus}
								onClick={handleCreateNew}
							>
								Create First Template
							</StudioButton>
						</div>
					</FloatPanel>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{customTemplates.map((tmpl) => {
							const totalItems = tmpl.sections.reduce(
								(sum, s) => sum + s.items.length,
								0,
							);
							const baselineTotal = tmpl.sections.reduce(
								(secSum, s) =>
									secSum +
									s.items.reduce((itSum, it) => itSum + it.lineTotal, 0),
								0,
							);

							return (
								<FloatPanel key={tmpl.id} elevation={1} padding={20}>
									<div className="flex flex-col justify-between h-full space-y-4">
										<div className="space-y-2">
											<div className="flex items-start justify-between gap-2">
												<h3
													className="font-semibold text-base"
													style={{
														fontFamily: "var(--font-display)",
														color: "var(--color-dashboard-text)",
													}}
												>
													{tmpl.name}
												</h3>
												<div className="flex items-center gap-1.5">
													<span
														className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
														style={{
															background: "var(--color-dashboard-divider)",
															color: "var(--color-dashboard-accent-dark)",
														}}
													>
														{tmpl.defaultTier}
													</span>
													<span
														className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
														style={{
															background:
																"var(--color-dashboard-warning-bg-soft)",
															color: "var(--color-dashboard-pill-awaiting)",
														}}
													>
														{tmpl.projectScope === "full_home"
															? "Full Home"
															: "Partial"}
													</span>
												</div>
											</div>

											<p
												className="text-xs line-clamp-2"
												style={{ color: "var(--color-dashboard-muted)" }}
											>
												{tmpl.description || "No description provided."}
											</p>

											<div
												className="flex items-center justify-between text-xs pt-2 border-t"
												style={{ borderColor: "var(--color-dashboard-border)" }}
											>
												<span style={{ color: "var(--color-dashboard-muted)" }}>
													{tmpl.sections.length} Sections · {totalItems} Items
												</span>
												<span
													className="font-semibold"
													style={{ color: "var(--color-dashboard-text)" }}
												>
													Baseline: {formatPricePaisa(baselineTotal)}
												</span>
											</div>
										</div>

										{/* Action buttons */}
										<div
											className="flex items-center justify-between gap-2 pt-2 border-t"
											style={{ borderColor: "var(--color-dashboard-border)" }}
										>
											<StudioButton
												variant="ghost"
												size="sm"
												icon={Eye}
												onClick={() => handlePreviewCustom(tmpl)}
											>
												Preview
											</StudioButton>

											<div className="flex items-center gap-1">
												<button
													type="button"
													onClick={() => handleDuplicate(tmpl.id)}
													title="Duplicate template"
													className="p-1.5 rounded hover:bg-[var(--color-dashboard-divider)] text-[var(--color-dashboard-muted)] hover:text-[var(--color-dashboard-text)]"
												>
													<Copy className="h-4 w-4" />
												</button>
												<button
													type="button"
													onClick={() => handleEdit(tmpl)}
													title="Edit template"
													className="p-1.5 rounded hover:bg-[var(--color-dashboard-divider)] text-[var(--color-dashboard-muted)] hover:text-[var(--color-dashboard-text)]"
												>
													<Pencil className="h-4 w-4" />
												</button>
												<button
													type="button"
													onClick={() => handleDelete(tmpl.id)}
													title="Delete template"
													className="p-1.5 rounded transition-colors text-dashboard-muted hover:text-dashboard-danger hover:bg-dashboard-surface"
												>
													<Trash2 className="h-4 w-4" />
												</button>
											</div>
										</div>
									</div>
								</FloatPanel>
							);
						})}
					</div>
				)
			) : /* System Starter Templates */
			systemLoadError ? (
				<FloatPanel elevation={1} padding={20}>
					<div role="alert" className="space-y-3">
						<p className="text-sm text-[var(--color-dashboard-text)]">
							Could not load Finishd starter templates. {systemLoadError}
						</p>
						<StudioButton onClick={loadData}>Try again</StudioButton>
					</div>
				</FloatPanel>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{systemTemplates.map((sys) => (
						<FloatPanel key={sys.id} elevation={1} padding={20}>
							<div className="flex flex-col justify-between h-full space-y-4">
								<div className="space-y-2">
									<div className="flex items-start justify-between gap-2">
										<h3
											className="font-semibold text-base"
											style={{
												fontFamily: "var(--font-display)",
												color: "var(--color-dashboard-text)",
											}}
										>
											{sys.name}
										</h3>
										<div className="flex items-center gap-1.5">
											<span
												className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
												style={{
													background: "var(--color-dashboard-divider)",
													color: "var(--color-dashboard-accent-dark)",
												}}
											>
												{sys.defaultTier}
											</span>
											<span
												className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
												style={{
													background: "var(--color-dashboard-surface)",
													color: "var(--color-dashboard-muted)",
												}}
											>
												Finishd Standard
											</span>
										</div>
									</div>

									<p
										className="text-xs line-clamp-2"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{sys.description ||
											"Platform starter estimate with pre-configured sections."}
									</p>
								</div>

								<div
									className="flex items-center justify-between gap-2 pt-2 border-t"
									style={{ borderColor: "var(--color-dashboard-border)" }}
								>
									<span
										className="text-[11px]"
										style={{ color: "var(--color-dashboard-placeholder)" }}
									>
										Ready-to-use template
									</span>

									<div className="flex items-center gap-2">
										<StudioButton
											variant="secondary"
											size="sm"
											icon={Copy}
											onClick={() => handleCustomizeSystemTemplate(sys)}
										>
											Customize Template
										</StudioButton>
									</div>
								</div>
							</div>
						</FloatPanel>
					))}
				</div>
			)}

			{/* Template Editor Dialog */}
			<TemplateEditorDialog
				open={isEditorOpen}
				onOpenChange={setIsEditorOpen}
				template={editingTemplate}
				onSaved={() => {
					loadData();
				}}
			/>

			{/* Line-by-Line Preview Dialog */}
			{previewData && (
				<Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
					<DialogContent
						className="max-h-[90vh] max-w-4xl overflow-y-auto"
						style={{
							background: "var(--color-dashboard-paper)",
							border: "1px solid var(--color-dashboard-border)",
						}}
					>
						<DialogHeader>
							<DialogTitle
								style={{
									fontFamily: "var(--font-display)",
									fontSize: 20,
									fontWeight: 600,
									color: "var(--color-dashboard-text)",
								}}
							>
								Template Preview: {previewData.template.name}
							</DialogTitle>
							<DialogDescription className="text-xs text-muted-foreground">
								Detailed line-by-line item specifications, units, and baseline
								rates.
							</DialogDescription>
						</DialogHeader>

						<div className="py-2">
							<BoqTemplatePreview preview={previewData} />
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
